import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Gift, Heart, Link2, Loader2, Send, Users, CheckCircle2 } from "lucide-react";
import { API } from "@/lib/api";

const GUIDE = "Practical Guide to Overcoming Relationship Anxiety & Welcoming Connection";
const inputCls = "rounded-full px-5 py-5 bg-white border-border";

const Bubble = ({ children, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35 }}
    className="flex items-start gap-3"
    data-testid={testId}
  >
    <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
      <Heart className="h-4 w-4 text-primary" />
    </div>
    <div className="rounded-2xl rounded-tl-sm bg-white border border-border/40 px-5 py-4 text-[15px] md:text-base text-foreground/85 leading-relaxed max-w-xl">
      {children}
    </div>
  </motion.div>
);

const UserReply = ({ children }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-end">
    <div className="rounded-2xl rounded-tr-sm bg-primary text-primary-foreground px-5 py-3 text-sm md:text-base max-w-md">{children}</div>
  </motion.div>
);

const Choice = ({ onClick, icon: Icon, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-white px-5 py-2.5 text-sm font-medium text-primary hover:bg-primary/5 hover:border-primary/60 transition-colors"
    data-testid={testId}
  >
    {Icon && <Icon className="h-4 w-4" />}
    {children}
  </button>
);

const copyShareLink = async () => {
  const url = `${window.location.origin}${window.location.pathname}#relationship-guide`;
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied — paste it into a text or message.");
  } catch {
    toast.message(url);
  }
};

const GuideForm = ({ mode, onDone }) => {
  const gift = mode === "gift";
  const [f, setF] = useState({ a: "", b: "", email: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (gift) {
        await axios.post(`${API}/referrals/guide-gift`, { sender_name: f.a.trim(), friend_name: f.b.trim() || undefined, friend_email: f.email.trim(), page_path: window.location.pathname });
      } else {
        await axios.post(`${API}/lead-magnet/relationship-guide`, { first_name: f.a.trim() || undefined, email: f.email.trim(), page_path: window.location.pathname });
      }
      onDone(gift ? f.b.trim() || f.email.trim() : f.email.trim());
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Please check the details and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.form initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit} className="ml-12 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl" data-testid={`guide-${mode}-form`}>
      <Input required={gift} maxLength={80} placeholder={gift ? "Your first name" : "Your first name (optional)"} value={f.a} onChange={set("a")} className={inputCls} data-testid={`guide-${mode}-name-input`} />
      {gift && <Input maxLength={80} placeholder="Their first name (optional)" value={f.b} onChange={set("b")} className={inputCls} data-testid="guide-gift-friend-name-input" />}
      <Input required type="email" placeholder={gift ? "Their email" : "Your email"} value={f.email} onChange={set("email")} className={`${inputCls} ${gift ? "sm:col-span-2" : ""}`} data-testid={`guide-${mode}-email-input`} />
      <Button type="submit" disabled={busy} className="sm:col-span-2 justify-self-start rounded-full px-7 py-5 bg-primary text-primary-foreground hover:bg-primary/90" data-testid={`guide-${mode}-submit-btn`}>
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
        {gift ? "Send them the gift" : "Send it to my inbox"}
      </Button>
    </motion.form>
  );
};

export const RelationshipGuideCompanion = ({ background = "bg-secondary/30" }) => {
  const [path, setPath] = useState(null);
  const [mode, setMode] = useState(null);
  const [sentTo, setSentTo] = useState(null);

  const reset = () => {
    setPath(null);
    setMode(null);
    setSentTo(null);
  };

  return (
    <section id="relationship-guide" className={`py-16 md:py-20 px-6 md:px-12 scroll-mt-24 ${background}`} data-testid="relationship-guide-companion">
      <div className="max-w-3xl mx-auto">
        <span className="inline-flex items-center gap-2 text-primary font-medium text-xs uppercase tracking-[0.18em]">
          <Users className="h-4 w-4" /> A moment of reflection
        </span>
        <h2 className="mt-3 font-serif text-2xl sm:text-3xl md:text-4xl font-semibold text-foreground leading-tight">
          Healing is easier <span className="italic text-primary">together</span>
        </h2>

        <div className="mt-8 flex flex-col gap-4">
          <Bubble testId="guide-bubble-opening">
            If you&rsquo;ve ever felt like you have to hold everything together on your own, you are far from alone. Relationship anxiety and hyper-independence are incredibly common — especially among women who&rsquo;ve learned that leaning on others isn&rsquo;t safe. The good news? Healing gets so much lighter when we support one another.
          </Bubble>
          <Bubble testId="guide-bubble-question">
            Do you happen to have a friend, family member, or colleague who often carries everything on their own or feels stuck in relationship anxiety?
          </Bubble>

          {!path && (
            <div className="ml-12 flex flex-wrap gap-2" data-testid="guide-choices">
              <Choice onClick={() => setPath("yes")} icon={Users} testId="guide-choice-yes">Yes, someone comes to mind</Choice>
              <Choice onClick={() => setPath("me")} icon={Heart} testId="guide-choice-me">Honestly… it might be me</Choice>
              <Choice onClick={() => setPath("later")} testId="guide-choice-later">Not right now</Choice>
            </div>
          )}

          <AnimatePresence>
            {path === "yes" && <UserReply key="r-yes">Yes, someone comes to mind</UserReply>}
            {path === "me" && <UserReply key="r-me">Honestly… it might be me</UserReply>}
            {path === "later" && <UserReply key="r-later">Not right now</UserReply>}
          </AnimatePresence>

          {path === "yes" && (
            <Bubble testId="guide-bubble-offer">
              What a caring thing to notice. We&rsquo;d love to gift you a complimentary copy of our <strong>{GUIDE}</strong>. Feel free to forward it directly to them — or use it yourself!
            </Bubble>
          )}
          {path === "me" && (
            <Bubble testId="guide-bubble-offer">
              Thank you for being honest — that takes real courage. Our <strong>{GUIDE}</strong> is yours, free. And if someone in your life could use it too, you can pass it along anytime.
            </Bubble>
          )}
          {path === "later" && (
            <Bubble testId="guide-bubble-later">
              That&rsquo;s completely okay. If someone comes to mind later, our free <strong>{GUIDE}</strong> will be right here — and you&rsquo;re always welcome to share this page.
            </Bubble>
          )}

          {path && !mode && !sentTo && (
            <div className="ml-12 flex flex-wrap gap-2" data-testid="guide-actions">
              {path !== "later" && (
                <Choice onClick={() => setMode(path === "me" ? "self" : "gift")} icon={Gift} testId="guide-action-primary">
                  {path === "me" ? "Send it to my inbox" : "Email it to them"}
                </Choice>
              )}
              {path !== "later" && (
                <Choice onClick={() => setMode(path === "me" ? "gift" : "self")} icon={path === "me" ? Gift : Heart} testId="guide-action-secondary">
                  {path === "me" ? "Gift it to someone" : "Keep a copy for myself"}
                </Choice>
              )}
              <Choice onClick={copyShareLink} icon={Link2} testId="guide-action-copy-link">Copy a share link</Choice>
              {path === "later" && <Choice onClick={reset} testId="guide-action-restart">Start over</Choice>}
            </div>
          )}

          {mode && !sentTo && <GuideForm key={mode} mode={mode} onDone={setSentTo} />}

          {sentTo && (
            <Bubble testId="guide-bubble-success">
              <span className="inline-flex items-center gap-2 font-semibold text-green-800">
                <CheckCircle2 className="h-4 w-4" /> {mode === "gift" ? `Your gift is on its way to ${sentTo}.` : `Your guide is on its way to ${sentTo}.`}
              </span>
              <span className="block mt-2">Thank you for helping healing ripple outward. 💛</span>
              <span className="mt-3 flex flex-wrap gap-2">
                <Choice onClick={() => { setMode("gift"); setSentTo(null); }} icon={Gift} testId="guide-success-gift-another">Gift another friend</Choice>
                <Choice onClick={copyShareLink} icon={Link2} testId="guide-success-copy-link">Copy a share link</Choice>
              </span>
            </Bubble>
          )}
        </div>
      </div>
    </section>
  );
};
